import { encrypt, type SubmitPostRequestParams } from "deso-protocol";

import { EditorMediaType } from "@/components/app/editor-v3/constants";
import type { EditorContextType } from "@/components/app/editor-v3/editor-context";
import { makeClient } from "@/contexts/apollo-context";
import type { Account } from "@/graphql/codegen/graphql";
import { AccessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyNameDocument } from "@/graphql/codegen/graphql";
import type { MediaItem } from "@/utils/media-utils";
import { encodeImageToBlurHash, extractURLs } from "@/utils/media-utils";
import { AUDIO_ONLY_TAG, generateMediaMetadata } from "@/utils/post-utils";

import {
  GATED_CONTENT_REQUIRED_MESSAGE,
  hasRequiredGatedPayload,
} from "../editor-gated-content-utils";

export const INLINE_IMAGE_UPLOAD_PENDING_MESSAGE =
  "Inline images are still uploading. Please wait for uploads to finish or remove failed inline images before submitting.";

const LOCAL_BLOB_URL_PATTERN = /\bblob:/i;

const hasLocalBlobUrl = (value?: string | null) =>
  Boolean(value && LOCAL_BLOB_URL_PATTERN.test(value));

const assertNoLocalBlobUrls = ({
  body,
  gatedBody,
  inlineImageMetadata,
}: Pick<EditorContextType, "body" | "gatedBody" | "inlineImageMetadata">) => {
  if (hasLocalBlobUrl(body) || hasLocalBlobUrl(gatedBody)) {
    throw new Error(INLINE_IMAGE_UPLOAD_PENDING_MESSAGE);
  }

  const hasLocalInlineMetadata = Object.values(inlineImageMetadata ?? {}).some(
    (metadata) =>
      hasLocalBlobUrl(metadata.url) ||
      hasLocalBlobUrl(metadata.thumbnailDataURL),
  );

  if (hasLocalInlineMetadata) {
    throw new Error(INLINE_IMAGE_UPLOAD_PENDING_MESSAGE);
  }
};

export const toPostRequestParams = async ({
  editorContext,
  user,
  initialData = {},
}: {
  editorContext: EditorContextType;
  user: Account;
  initialData?: Partial<SubmitPostRequestParams>;
}) => {
  const apolloClient = makeClient();

  const {
    media,
    gatedMedia,
    body,
    gatedBody,
    gated,
    subscriptionTier,
    poll,
    disabledURLs,
    inlineImageMetadata,
  } = editorContext;

  assertNoLocalBlobUrls({
    body,
    gatedBody,
    inlineImageMetadata,
  });

  const postData: Partial<SubmitPostRequestParams> = {
    ...initialData,
    PostExtraData: {
      isMarkdown: "true",
      ...(initialData.PostExtraData || {}),
    },
  };

  // Obviously, this should not happen, it is to satisfy TypeScript
  if (!postData.PostExtraData) {
    throw new Error("PostExtraData is not defined.");
  }

  const images = media.filter(({ type }) => type === EditorMediaType.Image);
  const imageURLs = extractURLs(images);

  const gatedImages = gatedMedia.filter(
    ({ type }) => type === EditorMediaType.Image,
  );
  const gatedImageURLs = extractURLs(gatedImages);

  const videos = media.filter(({ type }) => type === EditorMediaType.Video);
  const videoURLs = extractURLs(videos);

  const gatedVideos = gatedMedia.filter(
    ({ type }) => type === EditorMediaType.Video,
  );
  const gatedVideoURLs = extractURLs(gatedVideos);

  const audios = media.filter(({ type }) => type === EditorMediaType.Audio);
  const audioURLs = extractURLs(audios);

  const gatedAudios = gatedMedia.filter(
    ({ type }) => type === EditorMediaType.Audio,
  );
  const gatedAudioURLs = extractURLs(gatedAudios);

  const audioOnly =
    imageURLs?.length === 0 &&
    videoURLs?.length === 0 &&
    body.trim().length === 0 &&
    audioURLs?.length > 0;

  const finalBody = audioOnly ? AUDIO_ONLY_TAG : body;

  if (gated() && subscriptionTier) {
    if (!hasRequiredGatedPayload(editorContext)) {
      throw new Error(GATED_CONTENT_REQUIRED_MESSAGE);
    }

    const { data } = await apolloClient.query({
      query:
        AccessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyNameDocument,
      variables: {
        accessGroupOwnerPublicKey: user.publicKey,
        accessGroupKeyName: subscriptionTier.accessGroupKeyName,
      },
    });

    const accessGroupPublicKey =
      data?.accessGroupByAccessGroupOwnerPublicKeyAndAccessGroupKeyName
        ?.accessGroupPublicKey;

    if (!accessGroupPublicKey) {
      throw new Error(
        `Could not find access group for tier ${subscriptionTier.name}.`,
      );
    }

    if (gatedBody.length > 0) {
      postData.PostExtraData["SubscriptionEncryptedPostBody"] = await encrypt(
        accessGroupPublicKey,
        gatedBody,
      );
    }

    if (gatedImageURLs.length > 0) {
      postData.PostExtraData["SubscriptionEncryptedImageURLs"] = await encrypt(
        accessGroupPublicKey,
        JSON.stringify(gatedImageURLs),
      );
      postData.PostExtraData["SubscriptionBlurHash"] =
        await encodeImageToBlurHash(gatedImages[0]!.src);
    }

    if (gatedVideoURLs.length > 0) {
      postData.PostExtraData["SubscriptionEncryptedVideoURLs"] = await encrypt(
        accessGroupPublicKey,
        JSON.stringify(gatedVideoURLs),
      );

      if (!postData.PostExtraData["SubscriptionBlurHash"]) {
        postData.PostExtraData["SubscriptionBlurHash"] =
          await encodeImageToBlurHash(gatedVideos[0]!.src);
      }
    }

    if (gatedAudioURLs.length > 0) {
      postData.PostExtraData["SubscriptionEncryptedAudioURLs"] = await encrypt(
        accessGroupPublicKey,
        JSON.stringify(gatedAudioURLs),
      );
    }

    postData.PostExtraData["SubscriptionAccessGroupOwnerPublicKey"] =
      user.publicKey;
    postData.PostExtraData["SubscriptionAccessGroupKeyName"] =
      subscriptionTier.accessGroupKeyName;
    postData.PostExtraData["SubscriptionAccessGroupPublicKey"] =
      accessGroupPublicKey;
    postData.PostExtraData["AccessGroupDerivedPublicKey"] =
      subscriptionTier.accessGroupDerivedPublicKey;
    postData.PostExtraData["PriceToUnlockUsdCents"] = String(
      subscriptionTier.costUsdCents,
    );

    if (poll) {
      postData.PostExtraData["SubscriptionEncryptedPollWeightType"] =
        await encrypt(accessGroupPublicKey, poll.weightType);
      postData.PostExtraData["SubscriptionEncryptedPollOptions"] =
        await encrypt(
          accessGroupPublicKey,
          JSON.stringify(
            poll.options.filter((x) => !!x.value).map((option) => option.value),
          ),
        );
      postData.PostExtraData["SubscriptionEncryptedPollWeightTokenPublicKey"] =
        await encrypt(
          accessGroupPublicKey,
          poll.selectedAccount?.publicKey ?? "",
        );
    }
  }

  if (poll && !gated()) {
    postData.PostExtraData["PollWeightType"] = poll.weightType;
    postData.PostExtraData["PollOptions"] = JSON.stringify(
      poll.options.filter((x) => !!x.value).map((option) => option.value),
    );
    postData.PostExtraData["PollWeightTokenPublicKey"] =
      poll.selectedAccount?.publicKey ?? "";
  }

  if (disabledURLs) {
    postData.PostExtraData["DisabledEmbedURLs"] = JSON.stringify(disabledURLs);
  }

  if (images.length > 0) {
    const metadata = generateMediaMetadata(images, "image");
    Object.assign(postData.PostExtraData, metadata);
  }
  if (gatedImages.length > 0) {
    const metadata = generateMediaMetadata(gatedImages, "gated_image");
    Object.assign(postData.PostExtraData, metadata);
  }
  if (videos.length > 0) {
    const metadata = generateMediaMetadata(videos, "video");
    Object.assign(postData.PostExtraData, metadata);
  }
  if (gatedVideos.length > 0) {
    const metadata = generateMediaMetadata(gatedVideos, "gated_video");
    Object.assign(postData.PostExtraData, metadata);
  }
  if (audios.length > 0) {
    const metadata = generateMediaMetadata(audios, "audio");
    Object.assign(postData.PostExtraData, metadata);
  }
  if (gatedAudios.length > 0) {
    const metadata = generateMediaMetadata(gatedAudios, "gated_audio");
    Object.assign(postData.PostExtraData, metadata);
  }

  if (inlineImageMetadata && Object.keys(inlineImageMetadata).length > 0) {
    const metadata = generateMediaMetadata(
      Object.values(inlineImageMetadata).map((image) => ({
        metadata: image,
      })) as unknown as MediaItem[],
      "inline_image",
    );
    Object.assign(postData.PostExtraData, metadata);
  }

  if (audioURLs.length > 0) {
    postData.PostExtraData["AudioURLs"] = JSON.stringify(audioURLs);
  }

  return Object.assign(postData, {
    UpdaterPublicKeyBase58Check: user.publicKey,
    BodyObj: {
      Body: finalBody,
      ImageURLs: imageURLs,
      VideoURLs: videoURLs,
    },
  });
};
